"use client";

import { useCallback } from "react";
import type { PaymentPageData } from "@/lib/api/payment-page.schema";
import type { Dictionary } from "@/app/[lang]/dictionaries";
import type { Cart } from "@/hooks/use-cart";
import { Text } from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  formatAmount,
  formatDisplayAmount,
} from "@/app/[lang]/checkout/[token]/_components/payment.utils";
import { TruncatableDescription } from "@/components/truncatable-description";
import { ProductList } from "./product-list";
import { createPageAmountFormatter, getDisplayConversion } from "./page-amounts";
import { normaliseMarkets, type MarketPreview } from "@/lib/markets";
import type { Market } from "@/lib/api/checkout.schema";
import { MarketSelect } from "@/components/market-select";
import { Trash2 } from "lucide-react";

interface PageOrderSummaryProps {
  pageData: PaymentPageData;
  cart: Cart;
  dictionary: Dictionary;
  /** Custom amount (for fixed pages with allow_custom_amount) */
  customAmount?: number;
  onCustomAmountChange?: (amount: number) => void;
  /** Called when user presses Enter/Done on amount input to focus next field */
  onAmountSubmit?: () => void;
  /** Market whose currency prices are previewed in */
  previewMarket?: Market | null;
  onPreviewMarketChange?: (country: string) => void;
  /** Indicative conversion for the previewed market, once quoted */
  preview?: MarketPreview | null;
}

export function PageOrderSummary({
  pageData,
  cart,
  dictionary,
  customAmount = 0,
  onCustomAmountChange,
  onAmountSubmit,
  previewMarket = null,
  onPreviewMarketChange,
  preview = null,
}: PageOrderSummaryProps) {
  const { checkout, paymentPage } = dictionary;

  const allowCustomAmount =
    pageData.page_type === "fixed" &&
    pageData.allow_custom_amount === true &&
    pageData.min_amount != null &&
    pageData.max_amount != null;

  const minAmount = pageData.min_amount ?? 0;
  const maxAmount = pageData.max_amount ?? 0;

  const handleAmountInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!onCustomAmountChange) return;
      const raw = e.target.value.replace(/\D/g, "");
      const parsed = raw === "" ? 0 : parseInt(raw, 10);
      if (!isNaN(parsed)) onCustomAmountChange(parsed);
    },
    [onCustomAmountChange],
  );

  const formattedAmount =
    customAmount > 0 ? customAmount.toLocaleString("en-TZ") : "";
  const formattedPlaceholder =
    pageData.amount > 0
      ? pageData.amount.toLocaleString("en-TZ")
      : minAmount.toLocaleString("en-TZ");

  const displayAmount = allowCustomAmount ? customAmount : pageData.amount;

  // Page-currency amounts honour display_currency; a cart of items priced in
  // another currency is quoted in that currency. The custom-amount input
  // stays in pageData.currency since that is what the backend charges.
  const formatPrice = createPageAmountFormatter(pageData, cart, preview);
  const pageDisplay = pageData.display;
  // One market means there is nothing to switch between
  const markets = normaliseMarkets(pageData.markets);

  function formatPrimaryAmount(amount: number): string {
    // Fixed page with a server-provided converted amount - prefer the API value
    if (
      pageData.page_type === "fixed" &&
      !allowCustomAmount &&
      pageDisplay &&
      !preview &&
      getDisplayConversion(pageData)
    ) {
      return formatDisplayAmount(pageDisplay.amount, pageDisplay.currency);
    }
    return formatPrice(amount);
  }

  const amountError =
    allowCustomAmount && customAmount > 0
      ? customAmount < minAmount
        ? `${checkout.minimumAmount} ${formatAmount(minAmount, pageData.currency)}`
        : customAmount > maxAmount
          ? `${checkout.maximumAmount} ${formatAmount(maxAmount, pageData.currency)}`
          : null
      : null;

  return (
    <div className="flex h-full flex-col">
        {pageData.title && (
          <Text
            variant="h1"
            className="text-xl font-semibold leading-snug tracking-tight text-balance md:text-xl lg:text-2xl"
          >
            {pageData.title}
          </Text>
        )}

        {markets.length > 1 && previewMarket && onPreviewMarketChange && (
          <div className="mt-4 first:mt-0">
            <Label htmlFor="page-currency" className="mb-1.5 font-normal text-muted-foreground">
              {paymentPage.currency}
            </Label>
            <MarketSelect
              id="page-currency"
              variant="field"
              markets={markets}
              value={previewMarket}
              onChange={onPreviewMarketChange}
              label={paymentPage.currency}
            />
          </div>
        )}

        {/* Amount - fixed pages only; a catalog's total is on the pay button */}
        {pageData.page_type === "fixed" && (
          <div className="mt-4 first:mt-0">
            <Text variant="muted" className="mb-1.5">
              {allowCustomAmount ? checkout.amount : checkout.pay}
            </Text>
            {allowCustomAmount ? (
              <>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    {pageData.currency}
                  </span>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9,]*"
                    placeholder={formattedPlaceholder}
                    value={formattedAmount}
                    onChange={handleAmountInputChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        onAmountSubmit?.();
                      }
                    }}
                    aria-label={checkout.amount}
                    aria-invalid={!!amountError}
                    className="h-11 pl-12 tabular-nums"
                  />
                </div>
                {amountError ? (
                  <Text variant="muted" className="mt-1.5 text-xs text-destructive">
                    {amountError}
                  </Text>
                ) : (
                  <Text variant="muted" className="mt-1.5 text-xs">
                    {checkout.between} {formatAmount(minAmount, pageData.currency)}{" "}
                    {checkout.and} {formatAmount(maxAmount, pageData.currency)}
                  </Text>
                )}
              </>
            ) : (
              <Text
                variant="h2"
                className="border-0 pb-0 text-3xl font-semibold tracking-tight tabular-nums"
              >
                {formatPrimaryAmount(displayAmount)}
              </Text>
            )}
          </div>
        )}

        {pageData.description && (
          <div className="mt-6">
            <TruncatableDescription
              html={pageData.description}
              readMoreLabel={dictionary.paymentPage.readMore}
              readLessLabel={dictionary.paymentPage.readLess}
            />
          </div>
        )}

        {/* Catalog: product grid + cart */}
        {pageData.page_type === "catalog" && (
          <div className="mt-5">
            {/* Stock items to browse */}
            {pageData.items && pageData.items.length > 0 && (
              <ProductList
                items={pageData.items}
                cart={cart}
                dictionary={dictionary}
                formatPrice={formatPrice}
                market={previewMarket}
              />
            )}

            {/* Cart summary */}
            {cart.totalItems > 0 && (
              <>
                <Text variant="small" className="mt-5 mb-2.5 font-medium">
                  {paymentPage.orderSummary}
                </Text>
                <div className="divide-y rounded-lg border bg-background">
                  {cart.cartItems.map((entry) => (
                    <div
                      key={entry.item.id}
                      className="flex items-center justify-between gap-3 py-1.5 pr-1.5 pl-3"
                    >
                      <Text
                        variant="muted"
                        className="min-w-0 flex-1 truncate text-xs"
                      >
                        {entry.item.name} &times; {entry.quantity}
                      </Text>
                      <div className="flex shrink-0 items-center gap-1.5">
                        <Text
                          variant="small"
                          className="text-[13px] font-semibold"
                        >
                          {formatPrice(
                            entry.item.price * entry.quantity,
                            entry.item.currency,
                          )}
                        </Text>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8 text-muted-foreground hover:text-destructive"
                          onClick={() => cart.removeItem(entry.item.id)}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
    </div>
  );
}
